import { Table, Typography, Alert, Spin, Tag, Button, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { TrainingProjectDashboardStore } from '../../../stores';
import { PreparedPackageForImportDto } from '../../../types';
import { ColumnProps } from 'antd/lib/table';
import JsonDiffDialog from './JsonDiffDialog';

const { Text, Paragraph } = Typography;

type Props = {
    store: TrainingProjectDashboardStore;
    selectedPackages: string[];
};

const ImportPreviewStep: React.FC<Props> = ({ store, selectedPackages }) => {
    const [tablePage, setTablePage] = React.useState(1);
    const [diffDialogVisible, setDiffDialogVisible] = React.useState(false);
    const [selectedPackageForDiff, setSelectedPackageForDiff] = React.useState<PreparedPackageForImportDto | null>(
        null
    );

    // Load prepared packages when component mounts or selected packages change
    React.useEffect(() => {
        if (selectedPackages.length > 0 && store.project && store.trainingProject) {
            store.preparePackagesForImport(selectedPackages);
        }
    }, [selectedPackages, store]);

    React.useEffect(() => {
        setTablePage(1);
    }, [store.preparedPackagesForImport]);

    // Function to handle showing JSON diff dialog
    const handleShowDiff = (packageRecord: PreparedPackageForImportDto) => {
        setSelectedPackageForDiff(packageRecord);
        setDiffDialogVisible(true);
    };

    // Function to close the diff dialog
    const handleCloseDiffDialog = () => {
        setDiffDialogVisible(false);
        setSelectedPackageForDiff(null);
    };

    const columns: ColumnProps<PreparedPackageForImportDto>[] = [
        {
            key: 'packageName',
            dataIndex: 'packageName',
            title: 'Package Name',
            width: 200,
            render: (text: string, record: PreparedPackageForImportDto) => (
                <div>
                    <Text strong>{text}</Text>
                    {record.hasInvalidSchema && (
                        <div style={{ marginTop: 4 }}>
                            <Tag color="error">Invalid Schema</Tag>
                        </div>
                    )}
                    {!record.isDuplicate && record.existingExampleId && (
                        <div style={{ marginTop: 4 }}>
                            <Tooltip title={`Will update existing example with id ${record.existingExampleId}`}>
                                <Tag color="blue">Update</Tag>
                            </Tooltip>
                        </div>
                    )}
                    {record.isDuplicate && (
                        <div style={{ marginTop: 4 }}>
                            <Tooltip title={`Duplicate example of ${record.existingExampleId ?? 'existing example'}`}>
                                <Tag color="warning">Duplicate</Tag>
                            </Tooltip>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'input',
            dataIndex: 'input',
            title: 'Input Preview',
            width: 300,
            render: (text: string) => (
                <div style={{ maxHeight: 100, overflow: 'auto' }}>
                    <Text style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                        {text?.length > 500 ? `${text.substring(0, 500)}...` : text}
                    </Text>
                </div>
            )
        },
        {
            key: 'expectedOutput',
            dataIndex: 'expectedOutput',
            title: 'Expected Output Preview',
            render: (text: string) => (
                <div style={{ maxHeight: 100, overflow: 'auto' }}>
                    <Text style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                        {text?.length > 500 ? `${text.substring(0, 500)}...` : text}
                    </Text>
                </div>
            )
        },
        {
            key: 'actions',
            title: '',
            width: 120,
            render: (_, record: PreparedPackageForImportDto) => {
                const hasJsonDiff = store.jsonSchemaDifferences.has(record.packageId);

                if (!hasJsonDiff) {
                    return null;
                }

                return (
                    <Button
                        size="small"
                        type="default"
                        onClick={() => handleShowDiff(record)}
                        disabled={!hasJsonDiff}
                        title={hasJsonDiff ? 'View schema differences' : 'No differences available'}
                    >
                        View Diff
                    </Button>
                );
            }
        }
    ];

    const hasInvalidSchemas = store.preparedPackagesForImport.some(pkg => pkg.hasInvalidSchema);
    const validPackagesCount = store.preparedPackagesForImport.filter(
        pkg => !pkg.hasInvalidSchema && !pkg.isDuplicate
    ).length;
    const invalidPackagesCount = store.preparedPackagesForImport.length - validPackagesCount;

    if (store.isLoadingImportOperations) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text>Preparing packages for import...</Text>
                </div>
            </div>
        );
    }

    if (selectedPackages.length === 0) {
        return (
            <Alert
                message="No packages selected"
                description="Please go back to the previous step and select packages to import."
                type="info"
                showIcon
            />
        );
    }

    if (store.preparedPackagesForImport.length === 0) {
        return (
            <Alert
                message="No examples found"
                description="The selected packages do not contain any valid examples for import."
                type="warning"
                showIcon
            />
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Paragraph>
                    Review the examples that will be imported from the selected packages. Each package will create one
                    training example.
                </Paragraph>

                <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                    <div>
                        <Text strong>Total packages: </Text>
                        <Text>{store.preparedPackagesForImport.length}</Text>
                    </div>
                    <div>
                        <Text strong>Valid examples: </Text>
                        <Text type="success">{validPackagesCount}</Text>
                    </div>
                    {invalidPackagesCount > 0 && (
                        <div>
                            <Text strong>Invalid examples: </Text>
                            <Text type="danger">{invalidPackagesCount}</Text>
                        </div>
                    )}
                </div>

                {hasInvalidSchemas && (
                    <Alert
                        message="Some packages have invalid schemas"
                        description="Packages marked with 'Invalid Schema' will be skipped during import. Only valid examples will be imported."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
            </div>

            <Table
                data-id="import-preview-table"
                className="alpha-portal-table"
                dataSource={store.preparedPackagesForImport}
                columns={columns}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: false,
                    hideOnSinglePage: true,
                    showLessItems: true,
                    onChange: setTablePage,
                    current: tablePage
                }}
                rowKey={(record: PreparedPackageForImportDto) => record.packageId}
                scroll={{ y: 'calc(100vh - 450px)' }}
                rowClassName={(record: PreparedPackageForImportDto) =>
                    record.hasInvalidSchema ? 'table-row-disabled' : ''
                }
            />

            {/* JSON Diff Dialog */}
            <JsonDiffDialog
                visible={diffDialogVisible}
                onClose={handleCloseDiffDialog}
                packageName={selectedPackageForDiff?.packageName || ''}
                jsonDiff={
                    selectedPackageForDiff ? store.jsonSchemaDifferences.get(selectedPackageForDiff.packageId) : null
                }
            />
        </div>
    );
};

export default observer(ImportPreviewStep);
